import { useParams } from 'react-router-dom';
import { RiListSettingsLine } from '@remixicon/react';
import { BreadcrumbEntry, useRouteRef } from '@backstage/frontend-plugin-api';
import { type PulseFactCollectorInfo } from '@internal/backstage-plugin-pulse-common';
import {
  Header,
  Container,
  Table,
  useTable,
  Cell,
  CellText,
  Button,
  DialogTrigger,
  Popover,
  type ColumnConfig,
  SearchField,
  Flex,
} from '@backstage/ui';
import { usePulseChecks, usePulseFactCollectors } from '../../hooks';
import {
  integrationsDetailsSubRouteRef,
  integrationsRouteRef,
} from '../../routes';
import { PulseListSkeleton } from '../list-skeleton';
import {
  formatCollectorConfig,
  formatIncludedEntities,
  formatScheduleFrequency,
} from './formatters';
import { DependentChecksGrid } from './dependent-checks-grid';

type FactCollectorRow = {
  id: string;
  factCollectorId: string;
  entityFilter: string;
  schedule: string;
  collectorConfig: string;
};

const columns: ColumnConfig<FactCollectorRow>[] = [
  {
    id: 'factCollectorId',
    label: 'Fact Collector Name',
    isRowHeader: true,
    defaultWidth: '25fr',
    cell: item => <CellText title={item.factCollectorId} />,
  },
  {
    id: 'entityFilter',
    label: 'Scope Filter',
    defaultWidth: '35fr',
    cell: item => <CellText title={item.entityFilter} />,
  },
  {
    id: 'schedule',
    label: 'Schedule',
    defaultWidth: '25fr',
    cell: item => <CellText title={item.schedule} />,
  },
  {
    id: 'collectorConfig',
    label: 'Config',
    defaultWidth: '15fr',
    cell: item => (
      <Cell>
        <DialogTrigger>
          <Button
            variant="secondary"
            size="small"
            iconStart={<RiListSettingsLine />}
          >
            Show config
          </Button>
          <Popover placement="bottom" offset={8}>
            <div
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                maxWidth: '56rem',
                maxHeight: '24rem',
                overflowY: 'auto',
                padding: '0.75rem',
                fontFamily: 'monospace',
                fontSize: '0.85rem',
              }}
            >
              {item.collectorConfig}
            </div>
          </Popover>
        </DialogTrigger>
      </Cell>
    ),
  },
];

type IntegrationFactCollectorsTableProps = {
  data: FactCollectorRow[];
  error: Error | undefined;
};

const IntegrationFactCollectorsTable = ({
  data,
  error,
}: IntegrationFactCollectorsTableProps) => {
  const { tableProps, search } = useTable({
    mode: 'complete',
    data,
    searchFn: (items, query) => {
      const lowerQuery = query.toLowerCase();
      return items.filter(
        item =>
          item.factCollectorId.toLowerCase().includes(lowerQuery) ||
          item.entityFilter.toLowerCase().includes(lowerQuery) ||
          item.schedule?.toLowerCase().includes(lowerQuery),
      );
    },
    paginationOptions: { pageSize: data.length || 1 },
  });

  return (
    <Flex direction="column" gap="6">
      <SearchField
        aria-label="Search"
        placeholder="Search..."
        {...search}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        style={{ maxWidth: '36vw' }}
      />
      <Table
        columnConfig={columns}
        {...tableProps}
        emptyState="No fact collectors found"
        error={error}
        pagination={{ type: 'none' }}
      />
    </Flex>
  );
};

const toFactCollectorRow = (
  integrationId: string,
  factCollector: PulseFactCollectorInfo,
): FactCollectorRow => ({
  id: `${integrationId}:${factCollector.factCollectorId}`,
  factCollectorId: factCollector.factCollectorId,
  entityFilter: formatIncludedEntities(
    factCollector.collectorConfig.entityFilter,
  ),
  schedule: formatScheduleFrequency(factCollector.schedule.frequency),
  collectorConfig: formatCollectorConfig(factCollector.collectorConfig),
});

export const PulseIntegrationsDetails = () => {
  const { id = '' } = useParams();
  const getIntegrationsPath = useRouteRef(integrationsRouteRef);
  const getDetailsPath = useRouteRef(integrationsDetailsSubRouteRef);
  const {
    value: integrations,
    integration: integrationMetadata,
    loading,
    error,
  } = usePulseFactCollectors(id);
  const { value: checks } = usePulseChecks();
  const integrationDetails = integrations?.[0];
  const integration = integrationDetails ?? integrationMetadata;
  const integrationsPath = getIntegrationsPath?.() ?? '..';

  const breadcrumbLabel = integration?.title ?? id;
  const breadcrumbHref = getDetailsPath?.({ id }) ?? integrationsPath;

  const factCollectorRows =
    integrationDetails?.factCollectors.map(factCollector =>
      toFactCollectorRow(integrationDetails.integrationId, factCollector),
    ) ?? [];

  return (
    <BreadcrumbEntry entry={{ label: breadcrumbLabel, href: breadcrumbHref }}>
      <Header title={breadcrumbLabel} description={integration?.description} />
      <Container>
        {loading ? (
          <PulseListSkeleton />
        ) : (
          <Flex direction="column" gap="12">
            <IntegrationFactCollectorsTable
              data={factCollectorRows}
              error={error}
            />
            {integrationDetails && (
              <DependentChecksGrid
                factCollectors={integrationDetails.factCollectors}
                checks={checks ?? []}
              />
            )}
          </Flex>
        )}
      </Container>
    </BreadcrumbEntry>
  );
};
